import { useState, useCallback, useMemo } from "react";
import { Trans } from "react-i18next";
import { Disclaimer } from "components/extra/disclaimer/Disclaimer";
import { TranslationAnchor } from "components/common";
import { PaymentMethodSection } from "./PaymentMethodSection";
import { PhoneSection } from "./PhoneSection";
import { AmountSection } from "./AmountSection";
import classes from "./PayByText.module.scss";
import { useAddNotification, UseInput } from "hooks";
import { MakePaymentClick } from "../makePayment/MakePayment";
import { clearSlide } from "store/common/common-slice";
import { extractError, getEnumByValue, PAYMENT_METHOD_TYPE } from "utility";
import { ClientConfiguration, PaymentMethodConfiguration } from "store/config";
import { AccountRestrictions } from "store/account/account-state";
import { PaymentMethod } from "store/paymentMethods/paymentMethods-state";

interface PayByTextContentProps {
  phoneNumber: UseInput;
  paymentAmountTypeInput: UseInput;
  paymentAmount: UseInput;
  paymentMethodId: UseInput;
  paymentMethodSelection: UseInput;
  payByTextAccounts: Record<string, any>;
  isEnabled: boolean;
  paymentMethods: PaymentMethod[];
  save: (override?: Partial<any>) => Promise<void>;
  setShowResultDialog: (v: boolean) => void;
  setSaveResult: (v: "success" | "error" | null) => void;
  setErrorMessage: (v: string | null) => void;
  accountInformationId?: number;
  clientConfig: ClientConfiguration;
  paymentMethodTypesEnabled: { [key: string]: PaymentMethodConfiguration };
  restrictions: AccountRestrictions;
  dispatch: (...args: any[]) => void;
  t: (key: string, options?: any) => string;
}

export const PayByTextContent = ({
  phoneNumber,
  paymentAmountTypeInput,
  paymentAmount,
  paymentMethodId,
  paymentMethodSelection,
  payByTextAccounts,
  isEnabled,
  paymentMethods,
  save,
  setShowResultDialog,
  setSaveResult,
  setErrorMessage,
  accountInformationId,
  clientConfig,
  paymentMethodTypesEnabled,
  restrictions,
  dispatch,
  t,
}: PayByTextContentProps) => {
  const addNotification = useAddNotification();
  const [enabledFlag, setEnabledFlag] = useState(isEnabled);
  const handleToggle = () => {
    setEnabledFlag((prev) => !prev);
  };

  const currentPaymentMethodConfig = useMemo(() => {
    const selectedMethod = paymentMethods.find(
      (method) => method.id === Number(paymentMethodId.value),
    );

    const paymentMethodTypeKey = getEnumByValue(
      PAYMENT_METHOD_TYPE,
      selectedMethod?.paymentMethodType!,
    );

    if (paymentMethodTypeKey) {
      return selectedMethod && selectedMethod.paymentMethodType !== undefined
        ? paymentMethodTypesEnabled[paymentMethodTypeKey]
        : null;
    }
    return null;
  }, [paymentMethods, paymentMethodId.value, paymentMethodTypesEnabled]);

  const minPaymentAmount =
    currentPaymentMethodConfig?.payments?.minimumPaymentAmount;

  const maxPaymentAmount =
    currentPaymentMethodConfig?.payments?.maximumPaymentAmount;

  const [buttonClick, setbuttonClick] = useState<MakePaymentClick>({
    count: 0,
    scheduleFlag: true,
  });

  const updateButtonClickFromChild = useCallback(
    (updateFn: (prev: MakePaymentClick) => MakePaymentClick) => {
      setbuttonClick((prev) => updateFn(prev));
    },
    [],
  );

  const cancelButtonHandler = () => {
    dispatch(clearSlide());
  };

  const phoneNumberInUse = () => {
    return Object.values(payByTextAccounts).some(
      (entry) =>
        entry.phoneNumber === phoneNumber.value &&
        entry.accountInformationId !== accountInformationId,
    );
  };

  const handleSaveResult = async (saveFn: () => Promise<void>) => {
    try {
      await saveFn();
      setSaveResult("success");
      setErrorMessage(null);
    } catch (e) {
      setSaveResult("error");
      setErrorMessage(extractError(e, t("common:something_went_wrong"))?.[0]);
    }
    setShowResultDialog(true);
  };

  const handleSave = async () => {
    if (phoneNumberInUse()) {
      addNotification({
        type: "ERROR",
        translatedMessages: [t("payment:paybytext_phoneinuse")],
        closable: true,
      });
      return;
    }
    if (paymentMethodSelection.value === "new_method") {
      setbuttonClick((prev) => ({
        ...prev,
        count: prev.count + 1,
        scheduleFlag: true,
      }));
    } else {
      await handleSaveResult(() => save({ status: enabledFlag ? 1 : 0 }));
    }
  };

  const handleNewPaymentMethodAdded = async (newMethod: any) => {
    if (typeof newMethod.id === "number") {
      paymentMethodId.setValue(newMethod.id);
      await handleSaveResult(() =>
        save({ paymentMethodId: newMethod.id, status: enabledFlag ? 1 : 0 }),
      );
    }
  };

  return (
    <div key={accountInformationId} className={classes.PayByTextWrapper}>
      <div className={classes.ToggleWrapper}>
        <p className={classes.Heading}>{t("payment:paybytext_enable")}</p>
        <ft-switch
          aria-label="pay by text switch button"
          data-testid="apaybytext-toggle"
          onClick={handleToggle}
          {...(enabledFlag ? { selected: true } : {})}
        ></ft-switch>
      </div>
      <PhoneSection phoneNumber={phoneNumber} t={t} isEnabled={enabledFlag} />
      <ft-divider class={classes.Divider}></ft-divider>
      <AmountSection
        paymentAmountTypeInput={paymentAmountTypeInput}
        paymentAmount={paymentAmount}
        t={t}
        isEnabled={enabledFlag}
        minPaymentAmount={minPaymentAmount}
        maxPaymentAmount={maxPaymentAmount}
      />
      <ft-divider class={classes.Divider}></ft-divider>
      <PaymentMethodSection
        paymentMethods={paymentMethods}
        paymentMethodTypesEnabled={paymentMethodTypesEnabled}
        restrictions={restrictions}
        paymentMethodId={paymentMethodId}
        paymentMethodSelection={paymentMethodSelection}
        isEnabled={enabledFlag}
        t={t}
        handleNewPaymentMethodAdded={handleNewPaymentMethodAdded}
        buttonClick={buttonClick}
        updateButtonClickFromChild={updateButtonClickFromChild}
      />
      <ft-divider class={classes.Divider}></ft-divider>
      <Disclaimer
        translatedText={
          <Trans
            i18nKey="payment:paybytext_auth"
            values={{
              todaysDate: t("common:date_format", {
                value: new Date().toISOString(),
              }),
              contactPhone: clientConfig.primaryContactPhone,
              clientName: clientConfig.name,
            }}
            components={{ a: <TranslationAnchor /> }}
          />
        }
      />
      <ft-divider class={classes.Divider}></ft-divider>
      <Disclaimer
        translatedText={
          <Trans
            i18nKey="payment:autopay-disclaimer"
            components={{ a: <TranslationAnchor /> }}
          />
        }
      />
      <div className={classes.Buttons}>
        <ft-button
          class={[classes.Back, "outline-button"].join(" ")}
          label={t("common:cancel")}
          outlined
          onClick={cancelButtonHandler}
        ></ft-button>
        <ft-button
          class={classes.Make_payment}
          data-testid="save-button-autopay"
          label={t("common:save")}
          unelevated
          onClick={handleSave}
        ></ft-button>
      </div>
    </div>
  );
};
